<script>
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";

  export let value = "";
  export let options = [];
  export let label = "Edit value";
  export let open = false;
  export let placeholder = "";
  export let renameOnly = false;
  export let disabled = false;

  const dispatch = createEventDispatcher();
  let editing = false;
  let draft = value;
  let inputElement;
  let rootElement;

  $: if (!editing) draft = value;

  $: filteredOptions = options.filter((option) =>
    String(option).toLocaleLowerCase().includes(String(draft || "").trim().toLocaleLowerCase()),
  );
  $: if (!open) editing = false;

  async function startEditing(event) {
    event.stopPropagation();
    if (disabled) return;
    dispatch("open");
    draft = value;
    editing = true;
    await tick();
    inputElement?.focus();
    inputElement?.select();
  }

  function commit(nextValue = draft) {
    const normalizedValue = String(nextValue || "").trim();
    if (!normalizedValue || normalizedValue === "—") {
      draft = value;
      editing = false;
      return;
    }
    editing = false;
    if (normalizedValue !== value) dispatch("change", { value: normalizedValue });
  }

  function selectOption(option) {
    draft = option;
    commit(option);
  }

  function handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    } else if (event.key === "Escape") {
      draft = value;
      editing = false;
    }
  }

  function handleOutsideClick(event) {
    if (editing && rootElement && !rootElement.contains(event.target)) commit();
  }

  onMount(() => window.addEventListener("click", handleOutsideClick));
  onDestroy(() => window.removeEventListener("click", handleOutsideClick));
</script>

<div class="paxbot-inline-edit" bind:this={rootElement}>
  <button
    class="paxbot-inline-edit-value"
    type="button"
    {disabled}
    title={value || placeholder}
    on:click={startEditing}
  >
    <span class:placeholder={!value}>{value || placeholder}</span>
    <img src="/img/assistant/icons/edit.svg" alt="" />
  </button>

  {#if editing}
    <div class="paxbot-inline-edit-menu">
      <input
        bind:this={inputElement}
        class="paxbot-inline-edit-input"
        aria-label={label}
        placeholder={renameOnly ? `Enter ${label}` : `Search or enter ${label}`}
        bind:value={draft}
        on:keydown={handleKeydown}
      />
      {#if draft.trim() && !options.some((option) => option === draft.trim())}
        <button class="paxbot-inline-edit-option custom" type="button" on:mousedown|preventDefault={() => selectOption(draft)}>
          {renameOnly ? "Save" : "Use"} “{draft.trim()}”
        </button>
      {/if}
      {#if !renameOnly}
        <div class="paxbot-inline-edit-options">
          {#each filteredOptions as option}
            <button class="paxbot-inline-edit-option" type="button" on:mousedown|preventDefault={() => selectOption(option)}>
              {option}
            </button>
          {:else}
            <span class="paxbot-inline-edit-empty">No matching existing entry</span>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .paxbot-inline-edit {
    position: relative;
    width: 100%;
    min-width: 0;
  }

  .paxbot-inline-edit-value {
    display: flex;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    align-items: flex-start;
    gap: .35rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    white-space: normal;
    cursor: pointer;
  }

  .paxbot-inline-edit-value span {
    display: -webkit-box;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-height: 1.35;
  }

  .paxbot-inline-edit-value img {
    width: .85rem;
    height: .85rem;
    margin-top: .25rem;
    flex: 0 0 auto;
    opacity: 0;
    filter: brightness(0) invert(1);
    transition: opacity 120ms ease-in-out;
  }

  .paxbot-inline-edit-value .placeholder {
    color: var(--pax-text-secondary-new);
    font-style: italic;
  }

  .paxbot-inline-edit-value:hover img,
  .paxbot-inline-edit-value:focus-visible img {
    opacity: .8;
  }

  .paxbot-inline-edit-value:disabled {
    cursor: default;
  }

  .paxbot-inline-edit-value:disabled img {
    display: none;
  }

  .paxbot-inline-edit-input {
    width: 100%;
    min-width: 15rem;
    height: 2.25rem;
    box-sizing: border-box;
    padding: 0 .45rem;
    border: 1px solid var(--pax-white-new);
    border-radius: .35rem;
    outline: none;
    background: var(--pax-surface-control-new);
    color: var(--pax-white-new);
    font: inherit;
  }

  .paxbot-inline-edit-menu {
    position: absolute;
    z-index: 5;
    top: calc(100% + .35rem);
    left: 0;
    width: 18rem;
    padding: .65rem;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: .75rem;
    background: var(--pax-surface-raised-new);
    box-shadow: 0 .5rem 1.25rem rgba(0, 0, 0, .25);
  }

  .paxbot-inline-edit-options {
    display: flex;
    max-height: 12rem;
    flex-direction: column;
    gap: .2rem;
    margin-top: .45rem;
    overflow-y: auto;
    scrollbar-color: var(--pax-white-new) transparent;
    scrollbar-width: thin;
  }

  .paxbot-inline-edit-option {
    width: 100%;
    padding: .45rem .5rem;
    border: 0;
    border-radius: .35rem;
    background: transparent;
    color: var(--pax-white-new);
    font: inherit;
    font-size: .8rem;
    text-align: left;
    cursor: pointer;
  }

  .paxbot-inline-edit-option:hover,
  .paxbot-inline-edit-option.custom {
    background: var(--pax-surface-control-hover-new);
  }

  .paxbot-inline-edit-empty {
    padding: .45rem .5rem;
    color: var(--pax-text-secondary-new);
    font-size: .75rem;
  }
</style>
